import { useState, useRef, useEffect } from 'react'
import { ChevronUp, LogOut, Sun, Moon, Monitor } from 'lucide-react'

type Theme = 'light' | 'dark' | 'system'

export interface UserMenuProps {
  user?: {
    name: string
    email?: string
    avatarUrl?: string
  }
  teamName?: string
  isCollapsed: boolean
  onLogout?: () => void
  theme?: Theme
  onThemeChange?: () => void
}

export function UserMenu({ user, teamName, isCollapsed, onLogout, theme = 'system', onThemeChange }: UserMenuProps) {
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor
  const themeLabel = theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U'

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center gap-3 p-3
          hover:bg-zinc-100 dark:hover:bg-zinc-800
          transition-colors duration-150
          ${isCollapsed ? 'justify-center' : ''}
        `}
      >
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium bg-secondary-500">
            {initials}
          </div>
        )}

        {!isCollapsed && (
          <>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 truncate">
                {teamName}
              </p>
            </div>
            <ChevronUp
              className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={`
            absolute bottom-full mb-2
            ${isCollapsed ? 'left-full ml-2' : 'left-2 right-2'}
            bg-white dark:bg-zinc-900
            border border-zinc-200 dark:border-zinc-800
            rounded-lg shadow-lg
            py-1 min-w-[180px]
            z-50
          `}
        >
          <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {user?.name || 'User'}
            </p>
            {user?.email && (
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            )}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation()
              onThemeChange?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ThemeIcon className="w-4 h-4" />
            {themeLabel} mode
          </button>

          <button
            onClick={() => {
              setIsOpen(false)
              onLogout?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
